import Accordion from '@axa-japan/design-system-react/Accordion';
import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Checkbox from '@axa-japan/design-system-react/Checkbox';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon } from '@axa-japan/design-system-react/Icons';
import Message from '@axa-japan/design-system-react/Message';
import RadioTileGroupField from '@axa-japan/design-system-react/RadioTileGroupField';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import TextField from '@axa-japan/design-system-react/TextField';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import tw from 'tailwind-styled-components';
import { z } from 'zod';
import { isValidReceptionNo } from '../business/isValidReceptionNo';
import ErrorMessage from '../components/ErrorMessage';
import CheckboxTileGroupField, { CheckboxTileGroupFieldProps } from '../components/SystemDesign/CheckboxTileGroupField';
import useCreateSession from '../hooks/useCreateSession';
import useUpdateSession from '../hooks/useUpdateSession';
import { queryClient } from '../libs/queryClient';
import { Forms } from '../types/forms';
import { FormDataType } from '../utils/formUtils';
import { REGEX, changeToAlphanumeric, formatString } from '../utils/stringUtils';
// We may need to change the file location
import { useGetDocuments } from '../hooks/useGetDocuments';
import { AUTH_TYPE } from '../types/auth';
import { DOCUMENT_STATUS } from '../types/session';

const receptionNoLengthError = '書類受付番号（14桁）を入力してください。';
const receptionNumberRequiredError = '必須項目です。';
const receptionNoDigitError = '書類受付番号に誤りがあります。確認の上、修正してください';
const formTypeEmptyError = 'どちらかを選択してください。';
const formNotSelectedError = '代表的な書類とその他の書類の中から、書類を1つ以上選択してください。';
const inputReceptionNoMessage = '書類受付番号を入力してください。';
const selectFormTypeMessage = '個人/個人事業主または法人を選択してください。';
const selectAtLeast1FormMessage = '代表的な書類とその他の書類の中から、書類を1つ以上選択してください。';

export const CheckboxContainer = tw.div`
  flex-col
  px-4
  [&_label]:border-gray
  [&_label[data-state="checked"]]:border-none
  [&_span]:text-sm font-normal
  [&>div>div>div>label]:!flex
  [&>div>div>div>label]:!w-full
  [&>div>div>div]:!flex
  [&>div>div>div]:!w-full
`;

type FormSelectionFormValues = {
  receptionNo: string;
  formType: string;
  representativeDocs: string[];
  sessionId?: number;
};

type FormSelectionPageProps = {
  initialData: FormSelectionFormValues;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  onNext: (data: FormSelectionFormValues) => void;
  onBack: () => void;
  forms: Forms | undefined;
  connectionType: string;
};

const isFormUpdated = (previousValues: FormSelectionFormValues, newValues: FormSelectionFormValues) => {
  // Check if receptionNo or formType are different
  if (previousValues.receptionNo !== newValues.receptionNo || previousValues.formType !== newValues.formType) {
    return true;
  }

  // Check if representativeDocs lengths are different
  if (previousValues.representativeDocs.length !== newValues.representativeDocs.length) {
    return true;
  }

  // Check if any doc in representativeDocs is different
  return !newValues.representativeDocs.every((doc, index) => doc === previousValues.representativeDocs[index]);
};

const transformResponse = (data: Forms, formType: string) => {
  const documents = data.find((item) => item.type === formType);

  if (!documents) {
    return {
      type: formType,
      representativeDocs: [],
      categoryList: [],
    };
  }
  return {
    type: documents.type,
    representativeDocs: documents.representativeForms.map(({ id, name }) => ({
      title: name,
      value: id,
    })),
    categoryList: documents.categories.map(({ id, name, categoriesList: subcategoryList }) => ({
      id,
      label: name,
      subcategoryList: subcategoryList.map((sub) => ({
        id: sub.id,
        label: sub.name,
        documentList: sub.forms
          .map((form) => ({
            title: form.name,
            value: form.id,
          }))
          .filter(({ value }) => !documents.representativeForms.some((form) => form.id === value)),
      })),
    })),
  };
};

const FormSelectionPageSchema = z
  .object({
    receptionNo: z
      .string()
      .refine((value) => value.trim().length > 0, { message: receptionNumberRequiredError })
      .refine((value) => /^[a-zA-Z0-9]{14}$/.test(value), { message: receptionNoLengthError })
      .refine(isValidReceptionNo, { message: receptionNoDigitError }),
    formType: z.enum(['INDIVIDUAL', 'ENTERPRISE'], {
      errorMap: () => ({ message: formTypeEmptyError }),
    }),
    representativeDocs: z.array(z.string()).optional(), // Marked optional as it depends on formType selection
  })
  .refine(
    ({ formType, representativeDocs }) => {
      return formType && representativeDocs && representativeDocs.length > 0;
    },
    {
      message: formNotSelectedError,
      path: ['representativeDocs'],
    },
  );

const SwitchArrow: React.FC = () => {
  return (
    <svg width="10" height="12" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.00502 13.9845C3.57735 13.7341 2.38626 13.0541 1.43176 11.9444C0.477253 10.8346 0 9.52984 0 8.03011C0 7.31749 0.0976885 6.64937 0.293065 6.02576C0.488442 5.40201 0.756181 4.8057 1.09628 4.23681L2.19719 5.34144C2.01159 5.7397 1.8603 6.17023 1.74332 6.63303C1.62633 7.09583 1.56784 7.56153 1.56784 8.03011C1.56784 9.10576 1.89407 10.0377 2.54653 10.8259C3.19899 11.6139 4.01849 12.1357 5.00502 12.3912V13.9845ZM6.97487 14V12.4067C7.97481 12.1708 8.80101 11.6535 9.45347 10.8545C10.1059 10.0556 10.4322 9.1141 10.4322 8.03011C10.4322 6.82 10.0302 5.77459 9.22613 4.8939C8.42211 4.01321 7.42379 3.57286 6.23116 3.57286H5.92663L6.81105 4.46028L5.70231 5.57257L2.92543 2.78629L5.70231 0L6.81105 1.13246L5.94673 1.99971H6.23116C7.84643 1.99971 9.21173 2.59287 10.327 3.77918C11.4423 4.9655 12 6.38247 12 8.03011C12 9.5484 11.5215 10.8633 10.5644 11.9747C9.60724 13.086 8.41072 13.7611 6.97487 14Z"
        fill="#00008F"
      />
    </svg>
  );
};

const FormSelection: React.FC<FormSelectionPageProps> = ({ initialData, onNext, onBack, setFormData, forms, connectionType }) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormSelectionFormValues>({
    resolver: zodResolver(FormSelectionPageSchema),
    mode: 'onSubmit',
    defaultValues: initialData,
  }); // when session id is undefined , disable this api call
  const { data: documents } = useGetDocuments(initialData.sessionId as number);

  const uploadedDocs = documents?.filter((doc) => doc.documentStatus === DOCUMENT_STATUS.uploadCompleted);

  const customerRequestedDocs =
    documents?.filter((doc) => doc.documentStatus === DOCUMENT_STATUS.urlGenerated || doc.documentStatus === DOCUMENT_STATUS.smsSent) ?? [];
  // This will hold initialData and won't change throughout component's lifecycle
  const previousValues = useRef<FormSelectionFormValues>(initialData);

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isModalDocumentsOverwriteOpen, setIsModalDocumentsOverwriteOpen] = useState(false);
  const formTypeSelection = watch('formType');

  const { representativeDocs } = getValues();
  const responseData = transformResponse(forms || [], formTypeSelection);

  const createSession = useCreateSession();
  const updateSession = useUpdateSession();

  const handleConfirmSubmit: SubmitHandler<FormSelectionFormValues> = async (formData) => {
    let sessionId = undefined;

    if (!initialData.sessionId) {
      const data = await createSession.mutateAsync({ receptionNo: formData.receptionNo, formIds: formData.representativeDocs });
      sessionId = data.sessionId;
    } else if (initialData.sessionId && isFormUpdated(previousValues.current, formData)) {
      await updateSession.mutateAsync({
        sessionId: initialData.sessionId,
        formIds: formData.representativeDocs,
        receptionNo: formData.receptionNo,
      });

      queryClient.invalidateQueries({ queryKey: ['session', initialData.sessionId, 'documents'] });
    }

    onNext({ ...formData, sessionId: sessionId ? sessionId : initialData.sessionId });
  };

  const onSubmit: SubmitHandler<FormSelectionFormValues> = async (formData) => {
    if (
      customerRequestedDocs?.length > 0 &&
      ((connectionType === AUTH_TYPE.External && initialData.representativeDocs.length != customerRequestedDocs?.length) ||
        customerRequestedDocs?.some((item) => !initialData.representativeDocs.includes(item.id)))
    )
      setIsModalDocumentsOverwriteOpen(true);
    else handleConfirmSubmit(formData);
  };

  const onError = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 150); // Delays scrolling to the top to allow the page to settle and avoid jumping to the error field.
  };

  const resetForm = () => {
    const resetValues: Partial<FormDataType> = {
      receptionNo: '',
      formType: '',
      representativeDocs: [],
      sessionId: undefined,
    };
    reset(resetValues);
    setFormData((prev) => ({ ...prev, ...resetValues }));
  };

  const updateOptions = (checked: boolean, value: string, options: string[]) => {
    const checkedOptions = checked ? Array.from(new Set([...(options || []), value])) : (options || []).filter((val: string) => val !== value);
    const sortedRepresentativeDocs = checkedOptions.map(String).sort((a, b) => Number(a) - Number(b));

    setFormData((prev) => ({ ...prev, representativeDocs: sortedRepresentativeDocs }));
    return sortedRepresentativeDocs; // return items always in asc order
  };

  const updateFormType = (value: string) => {
    setFormData((prev) => ({ ...prev, representativeDocs: [], formType: value }));
    // setValue updates the form's state immediately, while setFormData is asynchronous
    setValue('representativeDocs', []);
    return value;
  };

  useEffect(() => {
    reset(initialData); // Reset defaultValues
  }, [reset]);

  const handleCloseModal = () => {
    resetForm();
    setIsWarningModalOpen(false);
  };

  return (
    <>
      <StatusModal
        id="status-modal"
        title="入力情報のリセット"
        open={isWarningModalOpen}
        setOpen={setIsWarningModalOpen}
        content="入力いただいた各項目、および、撮影またはファイル選択いただいた画像をすべて削除しますが、よろしいでしょうか。"
        buttons={[
          {
            text: 'キャンセル',
          },
          {
            text: 'OK',
            onClick: handleCloseModal,
          },
        ]}
      />
      <div>
        <Heading as="h1" className="pb-4">
          撮影する書類を選択
        </Heading>
        <Text size="sm">
          ※別の書類受付番号で書類を撮影する場合は「入力情報をリセットする」ボタンを押してください。
          <br />
          入力されている項目をリセットすることができます。
        </Text>
        <div className="text-right text-sm">
          <Button variant="text" icon={<SwitchArrow />} onClick={() => setIsWarningModalOpen(true)}>
            入力情報をリセットする
          </Button>
        </div>
      </div>

      {Object.keys(errors).length > 0 && (
        <Message title="入力内容をご確認ください" status="danger" hasCloseButton={false}>
          {Object.keys(errors).map((field) => {
            let message;
            if (field === 'receptionNo') {
              message = inputReceptionNoMessage;
            } else if (field === 'formType') {
              message = selectFormTypeMessage;
            } else {
              message = selectAtLeast1FormMessage;
            }

            return <p key={field}>{message}</p>;
          })}
        </Message>
      )}

      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <StatusModal
          id="status-modal"
          title="発行済みQRコード/URLが無効になります"
          open={isModalDocumentsOverwriteOpen}
          setOpen={setIsModalDocumentsOverwriteOpen}
          content={
            <div className="flex flex-col gap-5">
              <Text>お客さまに撮影依頼した書類のチェックが外れたため、次の書類を含む発行済みQRコード/URLを無効にしますがよろしいでしょうか。</Text>
              <ul className="list-disc ml-5">
                {customerRequestedDocs.map((document) => {
                  return (
                    <li key={`overwriting_${document.id}`}>
                      <Text>{document.name}</Text>
                    </li>
                  );
                })}
              </ul>
              <Text>再度お客さまに撮影依頼、又は、取扱者が撮影・送信してください。</Text>
            </div>
          }
          buttons={[
            {
              text: 'キャンセル',
            },
            {
              text: 'OK',
              onClick: () => handleConfirmSubmit(getValues()),
            },
          ]}
        />
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-1">
            <div className="flex w-full flex-row items-center gap-2">
              <Heading as="h3">書類受付番号を入力してください</Heading>
              <Badge variant="important" text="必須" />
            </div>
            <Text size="sm">※AXA Compassの一覧画面に表示されている書類受付番号をコピー＆ペーストにて転記をお願いします。</Text>
            <Text size="sm">
              ※書類受付番号が異なる書類は1度に撮影できません。
              {connectionType == AUTH_TYPE.External && '※同時申込（一括以外）がある場合は、一番若い番号を入力してください。'}
            </Text>
            <div className="w-1/2">
              <TextField
                {...register('receptionNo', {
                  onChange: (e) => {
                    e.target.value = changeToAlphanumeric(formatString(e.target.value, 14, REGEX.ANY_ALPHANUMERIC)).toUpperCase();
                  },
                })}
                showError={Boolean(errors.receptionNo)}
                maxLength={14}
                errorMessage={String(errors['receptionNo']?.message)}
                // onChange={updateReceptionNo}
              />
            </div>
          </div>
          {/* formType radio button group */}
          <div className="flex flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-2">
              <Heading as="h3">契約者区分を選択してください</Heading>
              <Badge variant="important" text="必須" />
            </div>
            <Controller
              name="formType"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <RadioTileGroupField
                  required={true}
                  itemsPerRow={2}
                  value={value}
                  options={[
                    { title: '個人/個人事業主', value: 'INDIVIDUAL' },
                    { title: '法人', value: 'ENTERPRISE' },
                  ]}
                  onChange={(e) => onChange(updateFormType(e.target.value))}
                  showError={Boolean(errors.formType)}
                  errorMessage={String(errors.formType?.message)}
                  disabled={uploadedDocs && uploadedDocs?.length > 0}
                />
              )}
            />
          </div>

          {formTypeSelection && (
            <Controller
              name={'representativeDocs'}
              control={control}
              render={({ field }) => (
                <>
                  <div className="flex flex-col gap-3">
                    <div className="flex w-full flex-row items-center gap-2">
                      <Heading as="h3">撮影する書類を選択してください（複数選択可）</Heading>
                      <Badge variant="important" text="必須" />
                    </div>
                    {errors.representativeDocs && <ErrorMessage message={String(errors.representativeDocs?.message)} />}
                    <Text weight="bold">代表的な書類</Text>

                    <CheckboxTileGroupField
                      key={formTypeSelection}
                      itemsPerRow={2}
                      value={initialData.representativeDocs}
                      options={
                        responseData.representativeDocs.map(({ title, value }) => ({
                          title,
                          value,
                          checked: initialData.representativeDocs.includes(value),
                          disabled: uploadedDocs?.some((doc) => doc.id === value) ? true : false,
                        })) as CheckboxTileGroupFieldProps['options']
                      }
                      onChange={(e) => {
                        const { value, checked } = e.target;
                        field.onChange(updateOptions(checked, value, field.value));
                      }}
                    />
                  </div>

                  {/* categories checkboxes */}
                  {responseData?.categoryList.map(({ id, subcategoryList, label }) => (
                    <div className="flex flex-col gap-1" key={id}>
                      <Heading as="h3">{label}</Heading>
                      <div className="px-2">
                        {subcategoryList.map(({ id, label, documentList }) => {
                          return (
                            <Accordion
                              key={`${formTypeSelection}_${id}`}
                              headerText={label}
                              displayStatusText
                              open={representativeDocs.some((doc) => documentList.some(({ value }) => value === doc)) || undefined}
                            >
                              <div className="grid grid-cols-2 gap-2 place-items-stretch">
                                {documentList.map(({ title, value }, index) => {
                                  return (
                                    <CheckboxContainer key={`key_${formTypeSelection}_${index}`}>
                                      <Checkbox
                                        value={value}
                                        label={title}
                                        disabled={uploadedDocs?.some((doc) => doc.id === value)}
                                        onChange={(e) => {
                                          const { value, checked } = e.target;
                                          field.onChange(updateOptions(checked, value, field.value));
                                        }}
                                        checked={representativeDocs.includes(value)}
                                      />
                                    </CheckboxContainer>
                                  );
                                })}
                              </div>
                            </Accordion>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
            />
          )}

          <div className="flex justify-center gap-3 mt-5">
            <div className="w-80">
              <Button
                variant="outlined"
                onClick={() => {
                  onBack();
                }}
                fullWidth
                icon={<ArrowIcon rotation="180" />}
                iconPosition="left"
              >
                戻る
              </Button>
            </div>
            <div className="w-80">
              <Button type="submit" fullWidth icon={<ArrowIcon />}>
                {connectionType === AUTH_TYPE.Internal ? '撮影方法選択画面へ' : '書類撮影依頼画面へ'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default FormSelection;
